import React, { useState } from 'react';
import { Container, Typography, Box, Paper, List, ListItem, ListItemIcon, ListItemText, Button, Divider, Grid, Card, CardContent, CardMedia } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowRight from '@mui/icons-material/ArrowRight';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import ChatIcon from '@mui/icons-material/Chat';
import DynamicInfographic from '../../../components/infographics/DynamicInfographic';
import FallRiskInfographics from '../../../components/infographics/FallRiskInfographics';
import ModuleSidebar from '../../../components/navigation/ModuleSidebar';
import { scrollToTop } from '../../../utils/scrollUtils';

const InitialAssessment = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('6.1');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showExplanations, setShowExplanations] = useState({});
  const [answerStatus, setAnswerStatus] = useState({});
  
  const handleBack = () => {
   navigate('/courses/choking-management');
    scrollToTop();
  };

  // Function to navigate to the next section
  const handleNextSection = () => {
    const currentSectionIndex = sections.findIndex(section => section.id === activeSection);
    if (currentSectionIndex < sections.length - 1) {
      const nextSection = sections[currentSectionIndex + 1].id;
      setActiveSection(nextSection);
      scrollToTop();
    }
  };

  // Define correct answers for each question
  const correctAnswers = {
    '6.1-1': 1, // To prevent choking and improve swallowing safety
    '6.1-2': 2, // Perform the Heimlich maneuver or chest thrusts
    '6.1-3': 1, // It allows caregivers to resuscitate a patient if the choking incident leads to unconsciousness
    '6.2-1': 2, // They are more likely to enter the airway and cause choking
    '6.2-2': 1, // To prevent choking and intervene if necessary
    '6.2-3': 1, // Pureed or soft foods
    '6.3-1': 2, // They are accessible to individuals with varying educational backgrounds
    '6.3-2': 0, // Pictorial demonstrations of choking prevention techniques
    '6.3-3': 1, // Evaluate knowledge retention after training sessions
  };

  const handleAnswerSelect = (sectionId, questionId, answerIndex) => {
    const key = `${sectionId}-${questionId}`;
    
    // Check if the correct answer has already been selected for this question
    // If so, don't allow further selections
    if (answerStatus[key] === true) {
      return;
    }
    
    // Store the selected answer
    setSelectedAnswers(prev => ({
      ...prev,
      [key]: answerIndex
    }));
    
    // Check if the selected answer is correct
    const isCorrect = answerIndex === correctAnswers[key];
    
    // Only show explanation if the correct answer is selected
    if (isCorrect) {
      setShowExplanations(prev => ({
        ...prev,
        [key]: true
      }));
    } else {
      // If incorrect, ensure explanation is hidden
      setShowExplanations(prev => ({
        ...prev,
        [key]: false
      }));
    }
    
    // Track if answer is correct or incorrect
    setAnswerStatus(prev => ({
      ...prev,
      [key]: isCorrect
    }));
  };

  // Function to handle section changes
  const handleSectionChange = (sectionId) => {
    setActiveSection(sectionId);
    scrollToTop();
  };
  
  // Define all sections for the module
  const sections = [
    {
      id: "6.1",
      title: "6.1 Patient and Caregiver Education",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ChatIcon sx={{ mr: 1, color: "#81c784" }} />
            6.1 Patient and Caregiver Education
          </Typography>

          <Typography paragraph>
            Educating patients and caregivers about choking risks and response techniques empowers them to act swiftly and effectively if an incident occurs. Effective education improves patient safety, reduces fear, and ensures that interventions are applied correctly during emergencies.
          </Typography>

          <Box
            sx={{
              bgcolor: "rgba(129, 199, 132, 0.1)",
              p: 2,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Typography variant="h6" gutterBottom>
              Key Aspects of Patient and Caregiver Education
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "white" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      Educating Patients at Risk of Choking
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Swallowing Techniques"
                          secondary="Eat slowly, take small bites, chew thoroughly, sit upright while eating"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Signs of Choking"
                          secondary="Recognize coughing, difficulty speaking, clutching throat"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "white" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ color: "#81c784", fontWeight: 600 }}
                    >
                      Training Family Members and Caregivers
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Heimlich Maneuver"
                          secondary="Learn abdominal thrusts and chest thrusts for different patients"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="CPR Training"
                          secondary="Learn chest compressions and rescue breaths"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <ArrowRight sx={{ color: "#81c784" }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Emergency Response"
                          secondary="Know how to activate EMS and provide clear instructions"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Quiz for Section 6.1 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is it important to educate patients about proper swallowing techniques?
                    </Typography>
                    <List dense>
                      {[
                        "To make meals more enjoyable",
                        "To prevent choking and improve swallowing safety",
                        "To ensure that patients eat more",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["6.1-1"] === index) {
                          if (index === correctAnswers["6.1-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["6.1-1"] &&
                          index === correctAnswers["6.1-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("6.1", "1", index)
                            }
                            disabled={
                              answerStatus["6.1-1"] === true &&
                              index !== correctAnswers["6.1-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["6.1-1"] === true &&
                                index !== correctAnswers["6.1-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["6.1-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Educating patients about safe swallowing techniques reduces the risk of choking and improves patient safety.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What should caregivers be trained to do if a patient with a complete airway obstruction is choking?
                    </Typography>
                    <List dense>
                      {[
                        "Wait until the patient becomes unconscious",
                        "Encourage the patient to cough harder",
                        "Perform the Heimlich maneuver or chest thrusts",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["6.1-2"] === index) {
                          if (index === correctAnswers["6.1-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["6.1-2"] &&
                          index === correctAnswers["6.1-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("6.1", "2", index)
                            }
                            disabled={
                              answerStatus["6.1-2"] === true &&
                              index !== correctAnswers["6.1-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["6.1-2"] === true &&
                                index !== correctAnswers["6.1-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["6.1-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Performing the Heimlich maneuver is the correct response to a complete airway obstruction.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is the benefit of training caregivers in CPR?
                    </Typography>
                    <List dense>
                      {[
                        "It helps improve billing accuracy",
                        "It allows caregivers to resuscitate a patient if the choking incident leads to unconsciousness",
                        "It makes patients feel more confident",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["6.1-3"] === index) {
                          if (index === correctAnswers["6.1-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["6.1-3"] &&
                          index === correctAnswers["6.1-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("6.1", "3", index)
                            }
                            disabled={
                              answerStatus["6.1-3"] === true &&
                              index !== correctAnswers["6.1-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["6.1-3"] === true &&
                                index !== correctAnswers["6.1-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["6.1-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          CPR training prepares caregivers to handle life-threatening choking incidents effectively.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "6.2",
      title: "6.2 Environmental and Dietary Modifications",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FitnessCenterIcon sx={{ mr: 1, color: "#81c784" }} />
            6.2 Environmental and Dietary Modifications
          </Typography>

          <Typography paragraph>
            Modifying the patient's environment and dietary habits is a proactive way to reduce choking risks. Special attention should be given to patients with swallowing difficulties, cognitive impairments, or mobility issues.
          </Typography>

          <Box
            sx={{
              mb: 3,
              p: 3,
              borderRadius: 2,
              bgcolor: "rgba(129, 199, 132, 0.1)",
              border: "1px solid rgba(129, 199, 132, 0.3)",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                color: "#2e7d32",
                fontWeight: 600,
                borderBottom: "1px solid rgba(129, 199, 132, 0.5)",
                pb: 1,
              }}
            >
              Key Aspects of Environmental and Dietary Modifications
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      1
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Modify Food Textures
                    </Typography>
                    <Typography variant="body2">
                      Recommend pureed, mashed, or soft foods for patients with swallowing difficulties. Use thickening agents for liquids and avoid risky foods like nuts and hard candies.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      2
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Supervise Mealtime
                    </Typography>
                    <Typography variant="body2">
                      Ensure proper positioning, encourage slow eating, and watch for signs of choking. Use adaptive utensils if needed and allow time for thorough chewing.
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                  <Box
                    sx={{
                      minWidth: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: "rgba(129, 199, 132, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#2e7d32", fontWeight: 600 }}
                    >
                      3
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600, color: "#2e7d32", mb: 0.5 }}
                    >
                      Remove Hazards
                    </Typography>
                    <Typography variant="body2">
                      Keep small objects away, use childproof containers, and store medications securely to prevent accidental ingestion.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Quiz for Section 6.2 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why should thin liquids be avoided for patients with swallowing difficulties?
                    </Typography>
                    <List dense>
                      {[
                        "They taste unpleasant",
                        "They are harder to digest",
                        "They are more likely to enter the airway and cause choking",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["6.2-1"] === index) {
                          if (index === correctAnswers["6.2-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["6.2-1"] &&
                          index === correctAnswers["6.2-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("6.2", "1", index)
                            }
                            disabled={
                              answerStatus["6.2-1"] === true &&
                              index !== correctAnswers["6.2-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["6.2-1"] === true &&
                                index !== correctAnswers["6.2-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["6.2-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Thin liquids can easily enter the airway, increasing the risk of choking.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is it important to supervise mealtime for high-risk patients?
                    </Typography>
                    <List dense>
                      {[
                        "To make sure they eat quickly",
                        "To prevent choking and intervene if necessary",
                        "To control food portions",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["6.2-2"] === index) {
                          if (index === correctAnswers["6.2-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["6.2-2"] &&
                          index === correctAnswers["6.2-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("6.2", "2", index)
                            }
                            disabled={
                              answerStatus["6.2-2"] === true &&
                              index !== correctAnswers["6.2-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["6.2-2"] === true &&
                                index !== correctAnswers["6.2-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["6.2-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Close supervision allows caregivers to recognize and respond to signs of choking immediately.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What type of food modification is recommended for patients with swallowing difficulties?
                    </Typography>
                    <List dense>
                      {[
                        "Raw and crunchy foods",
                        "Pureed or soft foods",
                        "Large pieces of meat",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["6.2-3"] === index) {
                          if (index === correctAnswers["6.2-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["6.2-3"] &&
                          index === correctAnswers["6.2-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("6.2", "3", index)
                            }
                            disabled={
                              answerStatus["6.2-3"] === true &&
                              index !== correctAnswers["6.2-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["6.2-3"] === true &&
                                index !== correctAnswers["6.2-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["6.2-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Soft and pureed foods are easier to swallow and reduce choking risks.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
    {
      id: "6.3",
      title: "6.3 Creating Educational Materials",
      content: (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: "primary.main",
              display: "flex",
              alignItems: "center",
            }}
          >
            <ChatIcon sx={{ mr: 1, color: "#81c784" }} />
            6.3 Creating Educational Materials
          </Typography>

          <Typography paragraph>
            Effective educational materials can significantly enhance learning about choking prevention and management. Well-designed resources reinforce key concepts, serve as reference tools, and help patients and caregivers retain critical information that could save lives during emergencies.
          </Typography>

          {/* Bar Chart for Educational Material Effectiveness */}
          <Box sx={{ mb: 4, p: 3, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)", border: "1px solid rgba(129, 199, 132, 0.3)" }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Effectiveness of Different Educational Formats
            </Typography>
            
            <Box sx={{ display: "flex", flexDirection: "column", mt: 3 }}>
              {/* This would be replaced with an actual chart component */}
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 180 }}>Video Demonstrations</Typography>
                <Box sx={{ height: 24, width: "85%", bgcolor: "#81c784", borderRadius: 1, ml: 2 }}></Box>
                <Typography variant="body2" sx={{ ml: 2 }}>85%</Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 180 }}>Interactive Workshops</Typography>
                <Box sx={{ height: 24, width: "80%", bgcolor: "#81c784", borderRadius: 1, ml: 2 }}></Box>
                <Typography variant="body2" sx={{ ml: 2 }}>80%</Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 180 }}>Illustrated Guides</Typography>
                <Box sx={{ height: 24, width: "75%", bgcolor: "#81c784", borderRadius: 1, ml: 2 }}></Box>
                <Typography variant="body2" sx={{ ml: 2 }}>75%</Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 180 }}>Mobile Applications</Typography>
                <Box sx={{ height: 24, width: "65%", bgcolor: "#81c784", borderRadius: 1, ml: 2 }}></Box>
                <Typography variant="body2" sx={{ ml: 2 }}>65%</Typography>
              </Box>
              
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Typography variant="body2" sx={{ minWidth: 180 }}>Text-only Materials</Typography>
                <Box sx={{ height: 24, width: "40%", bgcolor: "#81c784", borderRadius: 1, ml: 2 }}></Box>
                <Typography variant="body2" sx={{ ml: 2 }}>40%</Typography>
              </Box>
            </Box>
            
            <Typography variant="caption" sx={{ display: "block", mt: 2, textAlign: "center", fontStyle: "italic" }}>
              Based on knowledge retention and practical skill application after 30 days
            </Typography>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                    Key Design Principles
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Clear Visual Hierarchy" 
                        secondary="Use headings, bullet points, and emphasis to highlight important information" 
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Visual Support" 
                        secondary="Include relevant images, diagrams, and step-by-step illustrations"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Simplified Language" 
                        secondary="Use plain language at a 6th-8th grade reading level to ensure accessibility"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Focused Content" 
                        secondary="Prioritize essential information and avoid overwhelming details"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card variant="outlined" sx={{ height: "100%" }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: "#81c784", fontWeight: 600 }}>
                    Effective Distribution Strategies
                  </Typography>
                  
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Multi-channel Approach" 
                        secondary="Provide materials in print, digital, and video formats to accommodate different learning preferences"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Targeted Placement" 
                        secondary="Position materials in high-visibility areas such as dining rooms, nurse stations, and patient rooms"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Follow-up Integration" 
                        secondary="Incorporate materials into regular care planning and patient education sessions"
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary="Accessibility Considerations" 
                        secondary="Provide materials in multiple languages and formats for diverse populations"
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          <Box sx={{ my: 4, p: 3, borderRadius: 2, bgcolor: "rgba(129, 199, 132, 0.1)" }}>
            <Typography variant="h6" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
              Essential Components of Choking Education Materials
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "white", height: "100%" }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                    Prevention Resources
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Food preparation guidelines" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Safe swallowing techniques" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Proper positioning during meals" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="High-risk food identification" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "white", height: "100%" }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                    Response Training
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Visual choking recognition guide" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Step-by-step Heimlich maneuver instructions" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Back blow technique illustrations" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Emergency contact protocol" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Box sx={{ p: 2, borderRadius: 2, bgcolor: "white", height: "100%" }}>
                  <Typography variant="subtitle1" gutterBottom sx={{ color: "#2e7d32", fontWeight: 600 }}>
                    Follow-up Resources
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Skill practice checklists" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Knowledge refresher quizzes" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Incident documentation forms" />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <ArrowRight sx={{ color: "#81c784" }} />
                      </ListItemIcon>
                      <ListItemText primary="Resource directory for additional training" />
                    </ListItem>
                  </List>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Quiz for Section 6.3 */}
          <Box sx={{ mt: 4, mb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "#81c784", fontWeight: 600 }}
            >
              Quick Knowledge Check
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 1
                    </Typography>
                    <Typography variant="body2" paragraph>
                      Why is it important to use simplified language in choking education materials?
                    </Typography>
                    <List dense>
                      {[
                        "It reduces printing costs",
                        "It makes the content appear more professional",
                        "They are accessible to individuals with varying educational backgrounds",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["6.3-1"] === index) {
                          if (index === correctAnswers["6.3-1"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["6.3-1"] &&
                          index === correctAnswers["6.3-1"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("6.3", "1", index)
                            }
                            disabled={
                              answerStatus["6.3-1"] === true &&
                              index !== correctAnswers["6.3-1"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["6.3-1"] === true &&
                                index !== correctAnswers["6.3-1"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["6.3-1"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Using plain language at a 6th-8th grade reading level ensures that critical safety information is understandable to people with different educational levels, language skills, and cognitive abilities.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 2
                    </Typography>
                    <Typography variant="body2" paragraph>
                      According to research, which educational format is most effective for teaching choking prevention?
                    </Typography>
                    <List dense>
                      {[
                        "Pictorial demonstrations of choking prevention techniques",
                        "Text-only materials with detailed instructions",
                        "Verbal explanations from healthcare providers",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["6.3-2"] === index) {
                          if (index === correctAnswers["6.3-2"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["6.3-2"] &&
                          index === correctAnswers["6.3-2"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("6.3", "2", index)
                            }
                            disabled={
                              answerStatus["6.3-2"] === true &&
                              index !== correctAnswers["6.3-2"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["6.3-2"] === true &&
                                index !== correctAnswers["6.3-2"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["6.3-2"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Visual demonstrations through pictures and illustrations are highly effective for teaching physical techniques like choking prevention, as they clearly show proper hand placement and body positioning.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card
                  variant="outlined"
                  sx={{ height: "100%", bgcolor: "rgba(129, 199, 132, 0.05)" }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Question 3
                    </Typography>
                    <Typography variant="body2" paragraph>
                      What is an essential follow-up strategy after distributing educational materials?
                    </Typography>
                    <List dense>
                      {[
                        "Collect and recycle the materials after one month",
                        "Evaluate knowledge retention after training sessions",
                        "Replace the materials with new versions every week",
                      ].map((option, index) => {
                        let bgColor = "transparent";
                        let iconColor = "#bdbdbd";

                        if (selectedAnswers["6.3-3"] === index) {
                          if (index === correctAnswers["6.3-3"]) {
                            bgColor = "rgba(76, 175, 80, 0.2)";
                            iconColor = "#4caf50";
                          } else {
                            bgColor = "rgba(244, 67, 54, 0.2)";
                            iconColor = "#f44336";
                          }
                        } else if (
                          showExplanations["6.3-3"] &&
                          index === correctAnswers["6.3-3"]
                        ) {
                          bgColor = "rgba(76, 175, 80, 0.1)";
                          iconColor = "#4caf50";
                        }

                        return (
                          <ListItem
                            key={index}
                            button
                            onClick={() =>
                              handleAnswerSelect("6.3", "3", index)
                            }
                            disabled={
                              answerStatus["6.3-3"] === true &&
                              index !== correctAnswers["6.3-3"]
                            }
                            sx={{
                              bgcolor: bgColor,
                              borderRadius: 1,
                              opacity:
                                answerStatus["6.3-3"] === true &&
                                index !== correctAnswers["6.3-3"]
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            <ListItemIcon>
                              <CheckCircleIcon sx={{ color: iconColor }} />
                            </ListItemIcon>
                            <ListItemText primary={option} />
                          </ListItem>
                        );
                      })}
                    </List>
                    {showExplanations["6.3-3"] && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          bgcolor: "rgba(129, 199, 132, 0.1)",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Explanation:
                        </Typography>
                        <Typography variant="body2">
                          Following up with knowledge assessments helps determine if the information was understood and retained, and identifies areas where additional education may be needed.
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      ),
    },
  ];
  
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Back to Learning Modules
      </Button>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ color: "#81c784", fontWeight: 600 }}
        >
          Module 6: Preventive Measures and Patient Education
        </Typography>
        <Typography variant="subtitle1" sx={{ mb: 3, color: "text.secondary" }}>
          Explore strategies to prevent choking through patient education,
          environmental modifications, and dietary adjustments.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <ModuleSidebar
              sections={sections}
              activeSection={activeSection}
              onSectionChange={handleSectionChange}
              moduleTitle="Caregiver Best Practices"
            />
          </Grid>
          <Grid item xs={12} md={9}>
            {sections.find((section) => section.id === activeSection)?.content}

            {/* Module Buttons */}
            <Box
              sx={{ mt: 4, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back to Learning Modules
              </Button>
              {activeSection !== sections[sections.length - 1].id && (
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleNextSection}
                  sx={{ bgcolor: "#81c784", "&:hover": { bgcolor: "#4caf50" } }}
                >
                  Next Section
                </Button>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default InitialAssessment;
